import React from "react";
import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  HttpLink,
  ApolloProvider,
} from "@apollo/client";
import { WebSocketLink } from "@apollo/client/link/ws";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import { relayStylePagination } from "@apollo/client/utilities";
import { useAuth } from "@syos-music/auth";
import { useSnackbar } from "notistack";

import { IconButton } from "@mui/material";
import { Close as CloseIcon } from "mdi-material-ui";

function omitTypename(payload) {
  const cleanFn = (key, value) => (key === "__typename" ? undefined : value);
  return JSON.parse(JSON.stringify(payload), cleanFn);
}

export async function getSchema() {
  const ssrSchema = global.window && global.window.__APOLLO_SCHEMA__;
  if (ssrSchema) return ssrSchema;

  const result = await fetch(process.env.REACT_APP_GRAPHQL_SCHEMA_URL).then(
    (res) => res.json()
  );

  return result.data;
}

export async function getPossibleTypes(schema = getSchema()) {
  return (await schema).__schema.types.reduce((acc, supertype) => {
    if (supertype.possibleTypes) {
      acc[supertype.name] = supertype.possibleTypes.map(
        (subtype) => subtype.name
      );
    }

    return acc;
  }, {});
}

export function makeClient({
  clientConfig,
  httpLinkConfig,
  cacheConfig,
  getAccessToken,
  errorHandler,
} = {}) {
  const httpLink = new HttpLink({
    uri: process.env.REACT_APP_GRAPHQL_HTTP_ENDPOINT,
    ...httpLinkConfig,
  });

  const authLink = setContext(async (_, { headers }) => {
    const accessToken = await getAccessToken();
    if (!accessToken) return { headers };

    return {
      headers: {
        ...headers,
        authorization: `Bearer ${accessToken}`,
      },
    };
  });

  const omitTypenameLink = new ApolloLink((operation, forward) => {
    if (operation.variables) {
      operation.variables = omitTypename(operation.variables);
    }

    return forward(operation);
  });

  const wsLink = new WebSocketLink({
    uri: process.env.REACT_APP_GRAPHQL_WS_ENDPOINT,
    options: {
      reconnect: true,
      reconnectionAttempts: 50,
      lazy: true,
      timeout: 20000,
      connectionParams: async () => {
        const token = await getAccessToken();
        return token ? { authorization: `Bearer ${token}` } : {};
      },
    },
  });

  const errorLink = onError(errorHandler ?? console.error);

  const link = errorLink.concat(
    omitTypenameLink.split(
      // split based on operation type
      ({ query }) => {
        const def = query.definitions[0];

        // Check for @wsLink directive
        const wsLinkDirective = def.directives.find(
          (directive) => directive.name.value === "wsLink"
        );
        if (wsLinkDirective) {
          // Remove the @wsLink directive
          def.directives = def.directives.filter(
            (directive) => directive.name.value !== "wsLink"
          );
          return true;
        }

        return (
          def.kind === "OperationDefinition" && def.operation === "subscription"
        );
      },
      wsLink,
      authLink.concat(httpLink)
    )
  );

  return new ApolloClient({
    cache: new InMemoryCache({
      ...cacheConfig,
      typePolicies: {
        Query: {
          fields: {
            articles: relayStylePagination(["type"]),
            signatureMouthpieces: relayStylePagination(["filters"]),
            discountCodes: relayStylePagination(["search", "onlyActive"]),
            users: relayStylePagination(["search"]),
            invoices: relayStylePagination(["search", "hasPaymentId"]),
            mouthpieces: relayStylePagination([
              "search",
              "instrument",
              "saxophoneType",
              "clarinetType",
            ]),

            inventory: {
              merge(existing, incoming) {
                return incoming;
              },
            },
          },
        },
        Order: {
          fields: {
            events: relayStylePagination(),
            cart: {
              merge(existing, incoming) {
                return incoming;
              },
            },
          },
        },
        OrderState: {
          fields: {
            orders: relayStylePagination(["search"]),
          },
        },
        User: {
          fields: {
            orders: relayStylePagination(["paid"]),
          },
        },

        IntlString: { merge: true },
        Address: { merge: true },
        User_Profile: { merge: true },
        User_Affiliation: { merge: true },
        Statistics: { merge: true },
      },
    }).restore(global.window?.__APOLLO_STATE__),
    link,
    ...clientConfig,
  });
}

function ApolloError({ error }) {
  return (
    <div style={{ whiteSpace: "pre-wrap" }}>
      {error.graphQLErrors?.map(({ message }, idx) => (
        <div key={idx}>{message}</div>
      ))}

      {error.networkError && <div>{error.networkError.message}</div>}
      {error.networkError?.result?.errors.map(({ message, stack }, idx) => (
        <div key={idx}>{message}</div>
      ))}
    </div>
  );
}

export function SyosApolloProvider({ children }) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { getAccessToken } = useAuth();
  const [client, setClient] = React.useState();

  const errorHandler = React.useCallback(
    (error) => {
      const key = enqueueSnackbar(<ApolloError error={error} />, {
        variant: "error",
        persist: true,
        action: (
          <IconButton onClick={() => closeSnackbar(key)} size="large">
            <CloseIcon />
          </IconButton>
        ),
      });
    },
    [enqueueSnackbar, closeSnackbar]
  );

  React.useEffect(() => {
    (async () => {
      const possibleTypes = await getPossibleTypes();
      const apolloClient = makeClient({
        cacheConfig: { possibleTypes },
        getAccessToken,
        errorHandler,
      });
      setClient(apolloClient);
    })();
  }, [getAccessToken, errorHandler]);

  if (!client) return null;

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
